import { VisitStatus } from 'store/visits/types';

/**
 * Statuses a visit cannot come back from.
 *
 * A visit that has left the building, was refused, or never turned up is finished — offering to cancel or
 * reschedule it is offering an action that cannot mean anything. `CANCELED` is the one the rest of the app
 * already excluded; the other three were only ever missed because the detail view was hardcoded to render
 * as a freshly confirmed visit, so no status ever reached a decision.
 */
const TERMINAL_STATUSES: ReadonlySet<VisitStatus> = new Set([
  VisitStatus.CANCELED,
  VisitStatus.CHECKED_OUT,
  VisitStatus.NO_SHOW,
  VisitStatus.VISIT_DENIED,
]);

/** Whether the visit has reached a state it cannot leave. */
export const isTerminalVisitStatus = (status?: VisitStatus): boolean => !!status && TERMINAL_STATUSES.has(status);

/**
 * Whether cancelling still means something.
 *
 * An unknown status stays cancellable: a visit that has not been read yet is far more likely to be an
 * ordinary upcoming one than a finished one, and withholding the action on a live visit is the worse error.
 */
export const canCancelVisit = (status?: VisitStatus): boolean => !isTerminalVisitStatus(status);

/**
 * Whether rescheduling still means something.
 *
 * Stricter than cancelling by one status: once the visitor is on site the date is a matter of record, but
 * the visit can still be cut short.
 */
export const canEditVisit = (status?: VisitStatus): boolean =>
  !isTerminalVisitStatus(status) && status !== VisitStatus.CHECKED_IN;
